import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { JiraProjectOption } from '../jiraBoards/jiraBoardTypes';

type UpdateTeamBoardMappingDialogProps = {
  open: boolean;
  onClose: () => void;
  entityRef: string;
  groupDisplayName: string;
  /** The team's own declared keys (seed the selection with these, not inherited). */
  currentProjectKeys: string[];
  /** Keys the team only inherits from a parent; shown as context, never pre-selected. */
  inheritedProjectKeys?: string[];
  jiraProjects: JiraProjectOption[];
  /** Called after a successful save so callers can refetch catalog data. */
  onUpdated?: () => void;
};

type BoardUpdateStep = {
  label: string;
  status: 'ok' | 'failed';
};

type BoardUpdateResult = {
  noChange: boolean;
  prUrl?: string;
  prNumber?: number;
  projectKeys: string[];
  steps: BoardUpdateStep[];
};

export function UpdateTeamBoardMappingDialog({
  open,
  onClose,
  entityRef,
  groupDisplayName,
  currentProjectKeys,
  inheritedProjectKeys = [],
  jiraProjects,
  onUpdated,
}: UpdateTeamBoardMappingDialogProps) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [projectKeys, setProjectKeys] = useState<string[]>(currentProjectKeys);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [submitResult, setSubmitResult] = useState<BoardUpdateResult | undefined>();

  const sortedProjects = useMemo(() => {
    const byKey = new Map<string, JiraProjectOption>();
    for (const project of jiraProjects) {
      byKey.set(project.key, project);
    }
    // Keep currently-mapped keys selectable even if the Jira project list failed
    // to load or no longer includes them, so admins can still deselect/clear them.
    for (const key of projectKeys) {
      if (!byKey.has(key)) {
        byKey.set(key, { key, name: key });
      }
    }
    return [...byKey.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [jiraProjects, projectKeys]);

  useEffect(() => {
    if (open) {
      setProjectKeys(currentProjectKeys);
      setSubmitResult(undefined);
      setError(undefined);
    }
  }, [open, currentProjectKeys, entityRef]);

  const resetState = useCallback(() => {
    setProjectKeys(currentProjectKeys);
    setSubmitting(false);
    setError(undefined);
    setSubmitResult(undefined);
  }, [currentProjectKeys]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setError(undefined);
    try {
      const jiraBaseUrl = await discoveryApi.getBaseUrl('jira');
      const response = await fetchApi.fetch(`${jiraBaseUrl}/board-update`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entityRef, projectKeys }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Request failed with ${response.status}`);
      }

      const payload = (await response.json()) as BoardUpdateResult;
      setSubmitResult(payload);
      onUpdated?.();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : String(submitError));
    } finally {
      setSubmitting(false);
    }
  }, [discoveryApi, entityRef, fetchApi, onUpdated, projectKeys]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update team Jira boards</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
          Sets the Jira boards for <strong>{groupDisplayName}</strong> by opening a pull request
          against the invisible org descriptor. Nothing changes until that PR is reviewed and
          merged.
        </Typography>

        {error ? (
          <Box style={{ marginBottom: 16 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : null}

        {submitResult ? (
          <Box style={{ marginBottom: 8 }}>
            <Alert severity="success">
              {submitResult.noChange ? (
                <>These boards already match the descriptor — nothing to change.</>
              ) : (
                <>
                  Pull request{' '}
                  {submitResult.prUrl ? (
                    <Link href={submitResult.prUrl} target="_blank" rel="noopener noreferrer">
                      #{submitResult.prNumber}
                    </Link>
                  ) : (
                    <>#{submitResult.prNumber}</>
                  )}{' '}
                  opened. Review and merge it to apply the change.
                </>
              )}
            </Alert>
            <Box component="ul" style={{ marginTop: 12, paddingLeft: 20 }}>
              {submitResult.steps.map(step => (
                <Typography
                  key={step.label}
                  component="li"
                  variant="body2"
                  color={step.status === 'failed' ? 'error' : 'textSecondary'}
                >
                  {step.status === 'failed' ? '✕' : '✓'} {step.label}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null}

        {!submitResult && inheritedProjectKeys.length > 0 ? (
          <Box style={{ marginBottom: 16 }}>
            <Alert severity="info">
              This team has no boards of its own and currently inherits{' '}
              {inheritedProjectKeys.join(', ')} from a parent. Selecting keys here sets the
              team&apos;s own mapping; leave it empty to keep inheriting.
            </Alert>
          </Box>
        ) : null}

        {!submitResult ? (
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel id="team-jira-boards-label">Jira project keys</InputLabel>
            <Select
              labelId="team-jira-boards-label"
              multiple
              value={projectKeys}
              onChange={event => setProjectKeys(event.target.value as string[])}
              input={<OutlinedInput label="Jira project keys" />}
              renderValue={selected =>
                (selected as string[]).length > 0 ? (
                  <Box display="flex" flexWrap="wrap" gridGap={4}>
                    {(selected as string[]).map(key => (
                      <Chip key={key} label={key} size="small" />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    None selected
                  </Typography>
                )
              }
            >
              {sortedProjects.map(project => (
                <MenuItem key={project.key} value={project.key}>
                  {project.key} — {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="default">
          {submitResult ? 'Close' : 'Cancel'}
        </Button>
        {!submitResult ? (
          <Button
            onClick={() => void handleSubmit()}
            color="primary"
            variant="contained"
            disabled={submitting}
          >
            {submitting ? 'Saving…' : 'Save boards'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
